import * as React from 'react';
import { Grid, Paper, Skeleton, Stack } from '@mui/material';
import { GlassShadows } from '../glassmorphism/GlassmorphismStyles';

/* =========================
   CATEGORY CARD SKELEТОН
========================= */
const CategoryCardSkeleton = () => {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: { xs: '36px', md: '64px' },
        p: { xs: 3, md: 4 },
        height: { xs: 192, sm: 462 },
        width: { xs: 192, sm: 462 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: GlassShadows.glassUp,
        boxSizing: 'border-box',
        m: 'auto',
        background: 'inherit',
        border: 0,
      }}
    >
      <Stack spacing={2} alignItems='center' width='100%'>
        <Skeleton variant='circular' width={72} height={72} />
        <Skeleton
          variant='text'
          sx={{ fontSize: '1.2rem', width: '70%', mx: 'auto' }}
        />
        <Skeleton
          variant='text'
          sx={{ fontSize: '1.2rem', width: '45%', mx: 'auto' }}
        />
      </Stack>
    </Paper>
  );
};

/* =========================
   MAIN HERO SKELEТОН
========================= */
interface HeroSkeletonProps {
  isDark?: boolean;
}

export default function HeroSectionSkeleton({ isDark }: HeroSkeletonProps) {
  return (
    <>
      {/* HERO */}
      <Stack alignItems='center' textAlign='center' mb={{ xs: 4, md: 6 }}>
        <Skeleton
          variant='text'
          sx={{
            fontSize: { xs: '2.4rem', md: '64px' },
            width: { xs: '90%', sm: '70%', md: '55%' },
            mx: 'auto',
          }}
        />
        <Skeleton
          variant='text'
          sx={{ fontSize: '1rem', width: { xs: '95%', md: '75%' } }}
        />
        <Skeleton
          variant='text'
          sx={{ fontSize: '1rem', width: { xs: '88%', md: '68%' } }}
        />

        <Stack
          direction={{ xs: 'row', sm: 'row' }}
          spacing={1.5}
          sx={{
            width: { xs: '100%', md: '76%' },
            mt: 4,
            alignItems: 'center',
          }}
        >
          <Skeleton
            variant='rounded'
            height={52}
            sx={{
              flex: 1,
              borderRadius: '999px',
            }}
          />
          <Skeleton
            variant='rounded'
            height={52}
            sx={{ borderRadius: '999px', width: { xs: 96, sm: 120 } }}
          />
        </Stack>
      </Stack>

      {/* CATEGORIES */}
      <Grid
        mb={{ xs: 5, md: 7 }}
        sx={{
          justifyContent: 'center',
          display: 'flex',
          flexWrap: 'wrap',
          boxSizing: 'border-box',
          gap: '32px',
          maxWidth: 956,
          m: 'auto',
        }}
      >
        {[1, 2, 3, 4].map((item) => (
          <Grid item xs={12} md={6} key={item}>
            <CategoryCardSkeleton />
          </Grid>
        ))}
      </Grid>

      {/* MAP SECTION */}
      <Stack spacing={2} alignItems='center' mb={{ xs: 5, md: 7 }}>
        <Skeleton
          variant='text'
          sx={{
            fontSize: { xs: '2rem', md: '2.6rem' },
            width: { xs: '88%', md: '42%' },
          }}
        />
        <Skeleton
          variant='text'
          sx={{ fontSize: '1rem', width: { xs: '92%', md: '42%' } }}
        />

        <Paper
          elevation={0}
          sx={{
            width: '100%',
            overflow: 'hidden',
            borderRadius: { xs: '24px', md: '28px' },
            boxShadow: '0 10px 28px rgba(0,0,0,0.12)',
          }}
        >
          <Skeleton
            variant='rectangular'
            sx={{
              width: '100%',
              height: { xs: 300, sm: 380, md: 500 },
            }}
          />
        </Paper>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ width: '100%', justifyContent: 'center' }}
        >
          <Skeleton
            variant='rounded'
            width={140}
            height={34}
            sx={{ borderRadius: '999px' }}
          />
          <Skeleton
            variant='rounded'
            width={120}
            height={34}
            sx={{ borderRadius: '999px' }}
          />
          <Skeleton
            variant='rounded'
            width={160}
            height={34}
            sx={{ borderRadius: '999px' }}
          />
        </Stack>
      </Stack>

      {/* CTA */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 5 },
          mb: { xs: 5, md: 6 },
          borderRadius: { xs: '28px', md: '30px' },
          boxShadow: '0 10px 28px rgba(0,0,0,0.10)',
        }}
      >
        <Stack spacing={2} alignItems='center' textAlign='center'>
          <Skeleton
            variant='text'
            sx={{
              fontSize: { xs: '2rem', md: '2.4rem' },
              width: { xs: '80%', md: '34%' },
            }}
          />
          <Skeleton
            variant='text'
            sx={{ fontSize: '1rem', width: { xs: '95%', md: '62%' } }}
          />
          <Skeleton
            variant='text'
            sx={{ fontSize: '1rem', width: { xs: '88%', md: '52%' } }}
          />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{
              width: '100%',
              justifyContent: 'space-between',
              mt: 2,
            }}
          >
            <Skeleton
              variant='rounded'
              height={48}
              sx={{
                width: { xs: '100%', sm: 220 },
                borderRadius: '999px',
              }}
            />
            <Skeleton
              variant='rounded'
              height={48}
              sx={{
                width: { xs: '100%', sm: 220 },
                borderRadius: '999px',
              }}
            />
          </Stack>

          <Skeleton
            variant='text'
            sx={{ fontSize: '0.95rem', width: { xs: '90%', md: '44%' } }}
          />
        </Stack>
      </Paper>
    </>
  );
}
